import { ApiService } from '@supply-mover-2-0/core';
import type {
	SoftphoneDialerEntriesResponseDto,
	SoftphoneDialerEntryDto,
	SoftphoneFavoriteAddRequestDto,
	SoftphoneFavoritesOrderRequestDto,
	SoftphoneFavoritesResponseDto,
} from '@supply-mover-2-0/shared';

/**
 * PD-14960 — browser client for the softphone dialer's Recents, Frequent and
 * Favorites tabs (`/api/softphone/me/dialer/*`). Every call reads or changes
 * the signed-in rep's own lists; the server takes the rep from the session.
 */
export class SoftphoneDialerQueryService {
	public static async getRecents(
		limit?: number,
	): Promise<Array<SoftphoneDialerEntryDto>> {
		const query = limit != null ? `?limit=${String(limit)}` : '';
		const response = await ApiService.apiGet<SoftphoneDialerEntriesResponseDto>(
			`/softphone/me/dialer/recents${query}`,
		);
		return response.entries;
	}

	public static async getFrequent(args?: {
		days?: number;
		limit?: number;
	}): Promise<Array<SoftphoneDialerEntryDto>> {
		const params = new URLSearchParams();
		if (args?.days != null) params.append('days', String(args.days));
		if (args?.limit != null) params.append('limit', String(args.limit));
		const query = params.toString();
		const response = await ApiService.apiGet<SoftphoneDialerEntriesResponseDto>(
			`/softphone/me/dialer/frequent${query !== '' ? `?${query}` : ''}`,
		);
		return response.entries;
	}

	public static async getFavorites(): Promise<Array<SoftphoneDialerEntryDto>> {
		const response = await ApiService.apiGet<SoftphoneFavoritesResponseDto>(
			'/softphone/me/dialer/favorites',
		);
		return response.favorites;
	}

	/** Pin a number; resolves with the rep's whole favorites list. */
	public static async addFavorite(
		request: SoftphoneFavoriteAddRequestDto,
	): Promise<Array<SoftphoneDialerEntryDto>> {
		const response = await ApiService.apiPut<SoftphoneFavoritesResponseDto>(
			'/softphone/me/dialer/favorites',
			request,
		);
		return response.favorites;
	}

	/** Unpin a number; resolves with the rep's whole favorites list. */
	public static async removeFavorite(
		phoneE164: string,
	): Promise<Array<SoftphoneDialerEntryDto>> {
		const response = await ApiService.apiDelete<SoftphoneFavoritesResponseDto>(
			`/softphone/me/dialer/favorites?phoneE164=${encodeURIComponent(phoneE164)}`,
		);
		return response.favorites;
	}

	public static async reorderFavorites(
		request: SoftphoneFavoritesOrderRequestDto,
	): Promise<Array<SoftphoneDialerEntryDto>> {
		const response = await ApiService.apiPut<SoftphoneFavoritesResponseDto>(
			'/softphone/me/dialer/favorites/order',
			request,
		);
		return response.favorites;
	}
}
